import { IconButton, Popover, Tooltip } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { LuCheck } from "react-icons/lu";
import { MdCleaningServices } from "react-icons/md";

import { useTrackUpload } from "@/components/providers/AudiolabProvider/hooks";
import type {
  ProcessingTrack,
  ProcessingTrackStatus,
} from "@/components/providers/AudiolabProvider/AudiolabContext";
import ProgressPie from "@/components/ui/ProgressPie";
import { cn } from "@/lib/utils";

type ActivityTrackerProps = {
  isDark?: boolean;
};

const STATUS_TEXT: Record<ProcessingTrackStatus, string> = {
  pending: "Pending",
  generating: "Generating assets",
  done: "Done",
};

/** Filled circle with a white check — Marco's "Check circle filled" glyph. */
function FilledCheck({
  className,
  checkSize,
}: {
  className?: string;
  checkSize: number;
}) {
  return (
    <span
      className={cn(
        "flex flex-shrink-0 items-center justify-center rounded-full text-white",
        className,
      )}
    >
      <LuCheck size={checkSize} strokeWidth={3} />
    </span>
  );
}

function StatusIcon({ track }: { track: ProcessingTrack }) {
  if (track.status === "done") {
    return <FilledCheck className="h-5 w-5 bg-green-600" checkSize={12} />;
  }
  // Pending shows no trailing icon — the status text carries the state.
  if (track.status === "pending") return null;
  return (
    <ProgressPie
      value={track.completionPercentage}
      size={20}
      className="text-slate-700"
    />
  );
}

export default function ActivityTracker({ isDark }: ActivityTrackerProps) {
  const { processingTracks, removeTrack, clearFinished } = useTrackUpload();

  if (processingTracks.length === 0) return null;

  const activeCount = processingTracks.filter(
    (t) => t.status !== "done",
  ).length;
  const doneCount = processingTracks.length - activeCount;
  const allDone = activeCount === 0;

  // Aggregate progress across the batch (finished tracks count as complete) so
  // the pie reflects how far along everything is, not just one upload.
  const overallProgress =
    processingTracks.reduce(
      (sum, t) =>
        sum +
        (t.status === "done"
          ? 100
          : Math.max(0, Math.min(100, t.completionPercentage))),
      0,
    ) / processingTracks.length;

  return (
    <Popover.Root>
      <Tooltip content="Upload Activity">
        <Popover.Trigger>
          <IconButton
            className={cn(
              "cursor-pointer relative",
              !allDone &&
                (isDark
                  ? "text-white/70 hover:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white"
                  : "text-[#525966] hover:text-black data-[state=open]:bg-blue-50 data-[state=open]:text-black"),
              allDone &&
                "!rounded-full !bg-brand-primary/10 hover:!bg-brand-primary/20 data-[state=open]:!bg-brand-primary/20",
            )}
            variant="ghost"
            size="3"
            aria-label={
              allDone
                ? `Upload Activity, ${doneCount} finished`
                : `Upload Activity, ${activeCount} processing`
            }
          >
            {allDone ? (
              <FilledCheck
                className="h-[22px] w-[22px] bg-brand-primary"
                checkSize={14}
              />
            ) : (
              <ProgressPie value={overallProgress} size={22} />
            )}
            {allDone && doneCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-brand-primary text-white text-[10px] font-bold leading-[16px] text-center ring-2 ring-white"
                aria-hidden="true"
              >
                {doneCount > 9 ? "9+" : doneCount}
              </span>
            )}
          </IconButton>
        </Popover.Trigger>
      </Tooltip>

      <Popover.Content
        side="bottom"
        align="end"
        sideOffset={8}
        style={{ padding: 0, width: 360, maxWidth: "calc(100vw - 2rem)" }}
      >
        <div className="overflow-hidden rounded-lg bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Upload Activity
            </h3>
            <Tooltip content="Clear completed tasks">
              <button
                type="button"
                onClick={clearFinished}
                disabled={doneCount === 0}
                className="p-1 text-slate-500 transition-colors hover:text-slate-800 disabled:cursor-not-allowed disabled:text-slate-300"
                aria-label="Clear completed tasks"
              >
                <MdCleaningServices size={18} />
              </button>
            </Tooltip>
          </div>
          <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
            {processingTracks.map((track) => {
              const isDone = track.status === "done";
              return (
                <li
                  key={track.trackId}
                  className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/audiolab/tracks/$TRACK_ID"
                      params={{ TRACK_ID: track.trackId }}
                      onClick={
                        isDone ? () => removeTrack(track.trackId) : undefined
                      }
                      className="block truncate text-sm font-semibold text-slate-900 hover:underline"
                    >
                      {track.title}
                    </Link>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {STATUS_TEXT[track.status]}
                    </div>
                  </div>
                  <StatusIcon track={track} />
                </li>
              );
            })}
          </ul>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
